import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpRequest,
} from '@angular/common/http';
import { lastValueFrom } from 'rxjs';

import { environment } from 'src/environments/environment';

import { AuthPopup } from './auth-popup';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';

export interface ClientCredentials {
  clientId: string;
  clientSecret: string;
}

@Injectable({
  providedIn: 'root',
})
export class OAuthService {
  constructor(
    private httpClient: HttpClient,
    private userStateService: UserStateService,
    private companyStateService: CompanyStateService,
  ) {}

  _getRequestOptions(): any {
    var authorization =
      this.userStateService.getAccessToken().token_type === 'Bearer'
        ? this.userStateService.getAccessToken().token_type +
          ' ' +
          this.userStateService.getAccessToken().access_token
        : this.userStateService.getAccessToken().access_token;

    var headers = new HttpHeaders({
      authorization: authorization,
    });

    var requestOptions = {
      headers,
      withCredentials: true,
      responseType: 'json',
    };

    return requestOptions;
  }

  _getBody(provider, account, extend?: {}): any {
    var body: any = { provider };

    if (this._isUserAccount(provider)) {
      body.account = account || this.userStateService.getUserHash();
    } else {
      body.companyId = this.companyStateService.getSelectedCompanyId();
    }

    return Object.assign({}, extend, body);
  }

  _isUserAccount(provider) {
    return provider === 'twitter' ? false : true;
  }

  _getStatus(provider, account, scope) {
    const request = new HttpRequest(
      'POST',
      environment.OAUTH_TOKEN_PROVIDER_URL + 'status',
      this._getBody(provider, account, scope ? { scope } : null),
      this._getRequestOptions(),
    );

    return lastValueFrom(this.httpClient.request(request)).catch((error) => {
      console.debug('Could not get Status! ', error);

      throw error;
    });
  }

  _checkScopesMatch(
    scopes: string[] | null | undefined,
    allowedScopes: string[] | null | undefined,
  ): boolean {
    if (
      !Array.isArray(scopes) ||
      scopes.length === 0 ||
      !Array.isArray(allowedScopes) ||
      allowedScopes.length === 0
    ) {
      return false;
    }
    const allowed = new Set(allowedScopes);
    return scopes.every((scope) => allowed.has(scope));
  }

  getConnectionStatus(
    provider: string,
    scope: string | null = null,
    allowedNotAuthorizedScopes: string[] | null = null,
  ) {
    return this._getStatus(provider, null, scope).then((response: any) => {
      if (
        Array.isArray(response.body?.authenticated) &&
        response.body.authenticated.length
      ) {
        return { ...response.body, ...{ authenticated: true } };
      }

      // check if notAuthorizedScopes returned by OTP can be ignored
      if (
        this._checkScopesMatch(
          response.body?.notAuthorizedScopes,
          allowedNotAuthorizedScopes,
        )
      ) {
        return { ...response.body, ...{ authenticated: true } };
      }

      throw new Error('No authenticated on the response');
    });
  }

  getUsername(provider, account) {
    //account might be missing if component is new i.e. not yet configured
    if (!provider || !account) {
      return Promise.resolve({
        authenticated: null,
        username: null,
        accessToken: null,
      });
    }

    return this._getStatus(provider, account, '').then((response: any) => {
      if (response.body) {
        return {
          authenticated:
            Array.isArray(response.body.authenticated) &&
            response.body.authenticated.length > 0,
          username: response.body.username,
          accessToken: response.body.accessToken,
        };
      } else {
        throw new Error('Invalid response');
      }
    });
  }

  getAuthPopupInstance() {
    return new AuthPopup();
  }

  authenticate(provider, scope = null) {
    const id = this.getUserIdentifier(provider);

    let authPopup = this.getAuthPopupInstance();

    return authPopup
      .popup(provider, id, scope)
      .then((result: any) => {
        if (result && result.key) {
          return {
            key: result.key,
            username: result.username,
            account: result.account,
          };
        } else {
          throw new Error('No Key');
        }
      })
      .catch((error) => {
        console.debug(
          'Could not authenticate with OAuth Token Provider! ',
          error,
        );

        throw error;
      });
  }

  revoke(provider) {
    const request = new HttpRequest(
      'POST',
      environment.OAUTH_TOKEN_PROVIDER_URL + 'revoke',
      this._getBody(provider, null),
      this._getRequestOptions(),
    );

    return lastValueFrom(this.httpClient.request(request))
      .then((response: any) => {
        if (response && response.body && response.body.revoked) {
          return response.body.revoked;
        } else {
          throw new Error('Failed to revoke account');
        }
      })
      .catch((error) => {
        console.debug('Revoke failed', error);

        throw error;
      });
  }

  getUserIdentifier(provider) {
    return this._isUserAccount(provider)
      ? this.userStateService.getUserHash()
      : this.companyStateService.getSelectedCompanyId();
  }

  parseCompositeAccount(compositeAccount: string) {
    const [riseAccount, providerAccount] = compositeAccount?.split('-') || [];
    return { riseAccount, providerAccount };
  }

  async getAccounts(provider, account) {
    const requestOptions = this._getRequestOptions();

    requestOptions.params = new HttpParams({
      fromObject: {
        account,
        provider,
      },
    });

    const request = new HttpRequest(
      'GET',
      environment.OAUTH_TOKEN_PROVIDER_URL + 'accounts',
      requestOptions,
    );

    const response: any = await lastValueFrom(this.httpClient.request(request));
    return response?.body?.accounts || [];
  }

  async getCompanyCredentials(
    companyId: string,
    provider: string,
  ): Promise<ClientCredentials | null> {
    const url = `${environment.OAUTH_TOKEN_PROVIDER_URL}credentials/${companyId}/${provider}`;
    const request = new HttpRequest('GET', url, this._getRequestOptions());

    try {
      const response: any = await lastValueFrom(
        this.httpClient.request(request),
      );
      return response?.body || response || null;
    } catch (error: any) {
      // If credentials don't exist, return null
      if (
        error?.status === 404 ||
        error?.error?.code === 'CREDENTIALS_NOT_FOUND'
      ) {
        return null;
      }
      throw error;
    }
  }

  async createCompanyCredentials(
    companyId: string,
    provider: string,
    credentials: ClientCredentials,
  ): Promise<void> {
    const url = `${environment.OAUTH_TOKEN_PROVIDER_URL}credentials`;
    const options = this._getRequestOptions();

    // Include companyId and provider in the request body
    const body = {
      companyId,
      provider,
      ...credentials,
    };

    const request = new HttpRequest('POST', url, body, options);

    try {
      await lastValueFrom(this.httpClient.request(request));
    } catch (error: any) {
      throw error;
    }
  }

  async updateCompanyCredentials(
    companyId: string,
    provider: string,
    credentials: Partial<ClientCredentials>,
  ): Promise<void> {
    const url = `${environment.OAUTH_TOKEN_PROVIDER_URL}credentials/${companyId}/${provider}`;
    const options = this._getRequestOptions();

    const request = new HttpRequest('PUT', url, credentials, options);

    try {
      await lastValueFrom(this.httpClient.request(request));
    } catch (error: any) {
      throw error;
    }
  }

  async deleteCompanyCredentials(
    companyId: string,
    provider: string,
  ): Promise<void> {
    const url = `${environment.OAUTH_TOKEN_PROVIDER_URL}credentials/${companyId}/${provider}`;
    const request = new HttpRequest('DELETE', url, this._getRequestOptions());

    try {
      await lastValueFrom(this.httpClient.request(request));
    } catch (error: any) {
      throw error;
    }
  }
}
